import React, { ChangeEvent, useState } from 'react'
import { useSelector } from 'react-redux'
import { isDebtorType1 } from 'reducers/activecase/activecaseSlice'

import { useMutation } from '@apollo/client'
import {
  PLACE_NEW_COURSE_ORDER_ADD_SPOUSE_OR_JOINT_DEBTORS,
  PlaceNewCourseOrderAddSpouseOrJointDebtorsResponse,
  PlaceNewCourseOrderAddSpouseOrJointDebtorsVariables
} from 'share/mutations/courses'
import {
  Formik,
  FastField,
  Form,
  FormikHelpers,
  FormikProps,
  ErrorMessage
} from 'formik'
import * as Yup from 'yup'
import Button from 'react-bootstrap/Button'
import Select from 'components/features/common/select/Select'
import { dropdownStateOptions } from 'share/utils/utils'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import { setCasePlaceOrderUpdateFields } from 'reducers/activecase/activecaseSlice'
import {
  HasPlacedCourseOrderStatusType,
  CourseOrderStep,
  getCasePlaceOrderUpdateFields
} from 'share/domain/case'
import { CourseType } from 'share/domain/course'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import { ssnRegExp, phoneRegExp } from 'share/utils/utils'
import { SsnInput } from 'components/molecules/SsnInput'
import { PhoneInput } from 'components/molecules/PhoneInput'
import './styles.scss'

interface Values {
  addSpouse: boolean
  firstName: string
  lastName: string
  phoneNumber: string
  ssn: string
  emailAddress: string
  street: string
  zipCode: string
  city: string
  selectState: string
}

const validationSchema = Yup.object({
  firstName: Yup.string().when('addSpouse', {
    is: true,
    then: Yup.string().required('First Name is required'),
    otherwise: Yup.string()
  }),
  lastName: Yup.string().when('addSpouse', {
    is: true,
    then: Yup.string().required('Last Name is required'),
    otherwise: Yup.string()
  }),
  phoneNumber: Yup.string().when('addSpouse', {
    is: true,
    then: Yup.string()
      .matches(phoneRegExp, 'Phone number is not valid')
      .required('Phone Number is required'),
    otherwise: Yup.string()
  }),
  ssn: Yup.string().when('addSpouse', {
    is: true,
    then: Yup.string()
      .matches(ssnRegExp, 'Ssn is not valid')
      .required('SSN is required'),
    otherwise: Yup.string()
  }),
  emailAddress: Yup.string().when('addSpouse', {
    is: true,
    then: Yup.string()
      .email('Invalid email format')
      .required('Email Address is required'),
    otherwise: Yup.string()
  }),
  street: Yup.string().when('addSpouse', {
    is: true,
    then: Yup.string().required('Street is required'),
    otherwise: Yup.string()
  }),
  zipCode: Yup.string().when('addSpouse', {
    is: true,
    then: Yup.string().required('Zip Code is required'),
    otherwise: Yup.string()
  }),
  city: Yup.string().when('addSpouse', {
    is: true,
    then: Yup.string().required('City is required'),
    otherwise: Yup.string()
  }),
  selectState: Yup.string().when('addSpouse', {
    is: true,
    then: Yup.string().required('State is required'),
    otherwise: Yup.string()
  })
})

const CourseTabPlaceOrderAddSpouseOrJointDebtorForm = (): JSX.Element => {
  const isDebtor1 = useSelector(isDebtorType1)
  const dispatch = useAppDispatch()
  const activeCase = useAppSelector((state) => state.activeCase)
  const activeCourse = useAppSelector((state) => state.activeCourse)
  const [submitError, setSubmitError] = useState('')
  const [showCoDebtor, setShowCoDebtor] = useState(false)

  const [addCoDebtorToOrder, { loading }] = useMutation<
    PlaceNewCourseOrderAddSpouseOrJointDebtorsResponse,
    PlaceNewCourseOrderAddSpouseOrJointDebtorsVariables
  >(PLACE_NEW_COURSE_ORDER_ADD_SPOUSE_OR_JOINT_DEBTORS)

  async function addCoDebtorToOrderHelper(
    formValues: Values
  ): Promise<boolean> {
    let result = false

    const errorCasePlaceOrderUpdateFields = getCasePlaceOrderUpdateFields(
      isDebtor1,
      activeCourse?.activeTab === 'first',
      HasPlacedCourseOrderStatusType.ErrorOnAddSpouseorJointDebtorStep,
      CourseOrderStep.CourseOrderFailed
    )

    try {
      const input = {
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        courseType:
          activeCourse?.activeTab === 'first'
            ? CourseType.CreditCounseling
            : CourseType.DebtorEducation,
        addSpouse: formValues.addSpouse,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        ssn: formValues.ssn,
        street: formValues.street,
        city: formValues.city,
        stateCode: formValues.selectState,
        zipCode: formValues.zipCode,
        mobilePhoneNumber: formValues.phoneNumber,
        emailAddress: formValues.emailAddress,
        isDebtor1: isDebtor1
      }

      const addCoDebtorToOrderResult = await addCoDebtorToOrder({
        variables: { request: input }
      })

      if (
        !addCoDebtorToOrderResult.data?.orders
          .placeNewCourseOrderAddSpouseOrJointDebtors.success
      ) {
        setSubmitError('Error occurred while adding spouse to order')
        console.log('error 1')
        console.dir(errorCasePlaceOrderUpdateFields)
        dispatch(setCasePlaceOrderUpdateFields(errorCasePlaceOrderUpdateFields))
        result = false
      } else {
        result = true
        console.dir(
          addCoDebtorToOrderResult.data?.orders
            .placeNewCourseOrderAddSpouseOrJointDebtors
        )
        dispatch(
          setCasePlaceOrderUpdateFields(
            addCoDebtorToOrderResult.data?.orders
              .placeNewCourseOrderAddSpouseOrJointDebtors
          )
        )
      }
    } catch (e) {
      setSubmitError('Error occurred while adding spouse to order')
      result = false
      console.log('error 2')
      console.dir(errorCasePlaceOrderUpdateFields)
      dispatch(setCasePlaceOrderUpdateFields(errorCasePlaceOrderUpdateFields))
    }

    return result
  }

  const loadingDelay = useLoadingDelay(loading)

  function onSubmitWrapper(
    formik: FormikProps<Values>,
    e: React.FormEvent<HTMLFormElement>
  ) {
    e.preventDefault()
    formik.handleSubmit(e)
  }

  async function onSubmit(
    values: Values,
    { setSubmitting }: FormikHelpers<Values>
  ) {
    addCoDebtorToOrderHelper(values)
    setSubmitting(false)
  }

  const initialValues: Values = {
    addSpouse: false,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    ssn: '',
    emailAddress: '',
    street: '',
    zipCode: '',
    city: '',
    selectState: ''
  }

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      <Formik
        initialValues={initialValues}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {(formik) => {
          return (
            <Form
              className='form-container'
              onSubmit={(e) => onSubmitWrapper(formik, e)}
            >
              <div id='addSpouseEnterAccountDetails'>
                <div className='row'>
                  <div className='col-12 add-joint-spouse-container'>
                    <label htmlFor='addSpouse'>Add Joint Debtor</label>
                    <FastField
                      type='checkbox'
                      id='addSpouse'
                      name='addSpouse'
                      data-testid='add-spouse-checkbox-field'
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        formik.handleChange(e)
                        setShowCoDebtor(e.target.checked)
                      }}
                    />
                  </div>
                </div>

                {showCoDebtor && (
                  <>
                    <div className='row'>
                      <div className='form-group col-12 col-sm-12 col-md'>
                        <label htmlFor='firstName'>First Name</label>
                        <FastField
                          id='firstName'
                          name='firstName'
                          data-testid='add-spouse-first-name-field'
                          placeholder=''
                          className={
                            formik.errors.firstName
                              ? 'form-control is-invalid'
                              : 'form-control'
                          }
                          autoFocus
                        />
                        <ErrorMessage
                          name='firstName'
                          render={(msg) => (
                            <div
                              className='text-danger'
                              data-testid='add-spouse-first-name-field-error'
                            >
                              {msg}
                            </div>
                          )}
                        />
                      </div>
                      <div className='form-group col-12 col-sm-12 col-md'>
                        <label htmlFor='lastName'>Last Name</label>
                        <FastField
                          type='text'
                          id='lastName'
                          name='lastName'
                          data-testid='add-spouse-last-name-field'
                          placeholder=''
                          className={
                            formik.errors.lastName
                              ? 'form-control is-invalid'
                              : 'form-control'
                          }
                        />
                        <ErrorMessage
                          name='lastName'
                          render={(msg) => (
                            <div
                              className='text-danger'
                              data-testid='add-spouse-last-name-field-error'
                            >
                              {msg}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    <div className='row'>
                      <div className='form-group col-12 col-sm-12 col-md'>
                        <label htmlFor='phoneNumber'>Phone Number</label>
                        <FastField
                          id='phoneNumber'
                          name='phoneNumber'
                          as={PhoneInput}
                          data-testid='add-spouse-first-name-field'
                          placeholder=''
                          className={
                            formik.errors.phoneNumber
                              ? 'form-control is-invalid'
                              : 'form-control'
                          }
                        />
                        <ErrorMessage
                          name='phoneNumber'
                          render={(msg) => (
                            <div
                              className='text-danger'
                              data-testid='add-spouse-first-name-field-error'
                            >
                              {msg}
                            </div>
                          )}
                        />
                      </div>

                      <div className='form-group col-12 col-sm-12 col-md'>
                        <label htmlFor='ssn'>SSN</label>
                        <FastField
                          id='ssn'
                          name='ssn'
                          as={SsnInput}
                          data-testid='add-spouse-ssn-name-field'
                          placeholder=''
                          className={
                            formik.errors.ssn
                              ? 'form-control is-invalid'
                              : 'form-control'
                          }
                        />
                        <ErrorMessage
                          name='ssn'
                          render={(msg) => (
                            <div
                              className='text-danger'
                              data-testid='add-spouse-ssn-field-error'
                            >
                              {msg}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    <div className='row'>
                      <div className='form-group col-12 col-sm-12 col-md'>
                        <label htmlFor='emailAddress'>Email Address</label>
                        <FastField
                          id='emailAddress'
                          name='emailAddress'
                          data-testid='add-spouse-email-address-field'
                          placeholder=''
                          className={
                            formik.errors.emailAddress
                              ? 'form-control is-invalid'
                              : 'form-control'
                          }
                        />
                        <ErrorMessage
                          name='emailAddress'
                          render={(msg) => (
                            <div
                              className='text-danger'
                              data-testid='add-spouse-email-address-field-error'
                            >
                              {msg}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    <div className='row'>
                      <div className='form-group col-12 col-sm-12 col-md'>
                        <label htmlFor='street'>Street Address</label>
                        <FastField
                          as='textarea'
                          id='street'
                          name='street'
                          data-testid='add-spouse-mailing-address-field'
                          placeholder=''
                          className='form-control'
                        />
                        <ErrorMessage
                          name='street'
                          render={(msg) => (
                            <div
                              className='text-danger'
                              data-testid='add-spouse-mailing-address-field-error'
                            >
                              {msg}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    <div className='row last-row'>
                      <div className='form-group col-md col-sm-12'>
                        <label htmlFor='zipCode'>Zip Code</label>
                        <FastField
                          type='text'
                          id='zipCode'
                          name='zipCode'
                          data-testid='add-spouse-mailing-zip-code-field'
                          placeholder=''
                          className='form-control'
                        />
                        <ErrorMessage
                          name='zipCode'
                          render={(msg) => (
                            <div
                              className='text-danger'
                              data-testid='add-spouse-mailing-zip-code-field-error'
                            >
                              {msg}
                            </div>
                          )}
                        />
                      </div>
                      <div className='form-group col-md col-sm-12'>
                        <label htmlFor='city'>City</label>
                        <FastField
                          type='text'
                          id='city'
                          name='city'
                          data-testid='add-spouse-mailing-city-field'
                          placeholder=''
                          className='form-control'
                        />
                        <ErrorMessage
                          name='city'
                          render={(msg) => (
                            <div
                              className='text-danger'
                              data-testid='add-spouse-mailing-city-field-error'
                            >
                              {msg}
                            </div>
                          )}
                        />
                      </div>
                      <Select
                        selectStyling='form-group col-md col-sm-12'
                        fieldStyling={
                          formik.errors.selectState
                            ? 'form-control is-invalid'
                            : 'form-control'
                        }
                        label='State'
                        name='selectState'
                        data-testid='add-spouse-mailing-select-state-field'
                        options={dropdownStateOptions}
                      >
                        <ErrorMessage
                          name='selectState'
                          render={(msg) => (
                            <div
                              className='text-danger'
                              data-testid='add-spouse-mailing-select-state-field-error'
                            >
                              {msg}
                            </div>
                          )}
                        />
                      </Select>
                    </div>
                  </>
                )}
              </div>

              <Button
                type='submit'
                className='btn-submit btn-next'
                data-testid='add-spouse-mailing-next-button'
              >
                Next
              </Button>
            </Form>
          )
        }}
      </Formik>
      {submitError && <div>{submitError}</div>}
    </>
  )
}

export default CourseTabPlaceOrderAddSpouseOrJointDebtorForm
